import { Action } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { cn } from "@/common/helpers/utils";
import { getRelationPath, type RelationSubject } from "@/components/relation-link/relation-path";
import { EmptyValue } from "@/components/ui/empty-value";
import { ArrowUpRightIcon, type LucideIcon } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { Link } from "wouter";

interface SubComponentInfoCardRelation {
  subject: RelationSubject;
  uuid: string;
}

interface SubComponentInfoCardProps {
  icon: LucideIcon;
  label: string;
  value?: ReactNode;
  relation?: SubComponentInfoCardRelation;
}

const CARD_CLASS_NAME = "border-border bg-background flex h-fit items-center gap-3 rounded-lg border p-3";

export function SubComponentInfoCard({ icon: Icon, label, value, relation }: SubComponentInfoCardProps): ReactElement {
  const ability = useAbility();
  const isLink = relation !== undefined && ability.can(Action.Read, relation.subject);

  const content = (
    <>
      <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
        <Icon className="text-primary size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className={cn("font-semibold wrap-break-word", isLink && "group-hover:text-primary transition-colors")}>
          {value ?? <EmptyValue />}
        </div>
      </div>
    </>
  );

  if (!isLink) {
    return <div className={CARD_CLASS_NAME}>{content}</div>;
  }

  return (
    <Link
      to={getRelationPath(relation.subject, relation.uuid)}
      className={cn(
        CARD_CLASS_NAME,
        "group hover:border-primary/60 hover:bg-primary/5 transition-colors",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
      )}
    >
      {content}
      <ArrowUpRightIcon
        aria-hidden
        className="text-muted-foreground/70 group-hover:text-primary size-4 shrink-0 transition-[color,translate] group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </Link>
  );
}
